import type { EngineFactEnvelope } from "../report/contracts";
import type { EngineExecution } from "../engines/registry";
import type { SessionReport } from "../../features/report/session";
import {
  isLegalDisclaimer,
  translateBlockedReason,
  translateFailedReason,
} from "./blocked-reasons";
import { translateCitations } from "./citations";
import { buildComparisonSection } from "./comparison";
import { engineDisplayFacts } from "./engine-facts";
import { engineTitleVi } from "./engine-labels";
import { buildReportHeader } from "./header";
import { buildRecommendationCoverage } from "./recommendations";
import { guardVietnameseDisplayText } from "./text-guard";
import type {
  VietnameseMethodSection,
  VietnameseReportProjection,
} from "./types";

function confidenceLevelVi(confidence: number): string {
  if (confidence < 0.34) return "mức thấp";
  if (confidence < 0.67) return "mức trung bình";
  return "mức cao";
}

function traceVi(envelope: EngineFactEnvelope): readonly string[] {
  return Object.freeze(
    envelope.ruleTrace.map(
      (entry) =>
        guardVietnameseDisplayText(
          entry.description,
          "Bước tính toán kỹ thuật nội bộ.",
        ).textVi,
    ),
  );
}

function warningsVi(envelope: EngineFactEnvelope): readonly string[] {
  return Object.freeze(
    envelope.warnings
      .map(
        (warning) =>
          guardVietnameseDisplayText(
            warning,
            "Có lưu ý kỹ thuật chưa hiển thị được.",
          ).textVi,
      )
      .filter((warningVi) => !isLegalDisclaimer(warningVi)),
  );
}

function buildCompletedSection(
  execution: Extract<EngineExecution, { status: "completed" }>,
): VietnameseMethodSection {
  const envelope = execution.envelope;
  const facts = engineDisplayFacts(envelope);

  return Object.freeze({
    engine: execution.engine,
    titleVi: engineTitleVi[execution.engine],
    status: "sẵn_sàng",
    summaryVi: `Đã tính xong ${facts.length} dữ kiện kỹ thuật; độ tin cậy kỹ thuật ở ${confidenceLevelVi(
      envelope.confidence,
    )} và chưa được chuyên gia ký duyệt.`,
    facts,
    warningsVi: warningsVi(envelope),
    traceVi: traceVi(envelope),
  });
}

function buildBlockedSection(
  execution: Extract<EngineExecution, { status: "blocked" }>,
): VietnameseMethodSection {
  const reasonVi = translateBlockedReason(execution.engine, execution.reason);

  return Object.freeze({
    engine: execution.engine,
    titleVi: engineTitleVi[execution.engine],
    status: "bị_chặn",
    summaryVi: "Chưa đủ căn cứ chuyên gia để tính toán.",
    facts: Object.freeze([]),
    warningsVi: Object.freeze([reasonVi]),
    traceVi: Object.freeze([]),
  });
}

function buildFailedSection(
  execution: Extract<EngineExecution, { status: "failed" }>,
): VietnameseMethodSection {
  const reasonVi = translateFailedReason();

  return Object.freeze({
    engine: execution.engine,
    titleVi: engineTitleVi[execution.engine],
    status: "lỗi",
    summaryVi: "Có lỗi kỹ thuật khi tính toán; kết quả chưa được hiển thị.",
    facts: Object.freeze([]),
    warningsVi: Object.freeze([reasonVi]),
    traceVi: Object.freeze([]),
  });
}

function buildMethodSection(
  execution: EngineExecution,
): VietnameseMethodSection {
  switch (execution.status) {
    case "completed":
      return buildCompletedSection(execution);
    case "blocked":
      return buildBlockedSection(execution);
    case "failed":
      return buildFailedSection(execution);
  }
}

function completedEnvelopes(
  executions: readonly EngineExecution[],
): readonly EngineFactEnvelope[] {
  return executions.flatMap((execution) =>
    execution.status === "completed" ? [execution.envelope] : [],
  );
}

export function buildVietnameseReportProjection(
  report: SessionReport,
): VietnameseReportProjection {
  const envelopes = completedEnvelopes(report.executions);
  const methods = report.executions.map(buildMethodSection);
  const allSourceRefs = envelopes.flatMap((envelope) => envelope.sourceRefs);

  return Object.freeze({
    header: buildReportHeader(report),
    methods: Object.freeze(methods),
    comparison: buildComparisonSection(envelopes),
    recommendations: buildRecommendationCoverage(envelopes),
    citations: translateCitations(allSourceRefs),
  });
}
